import * as React from "react";
import styled from "styled-components";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import MailIcon from "@material-ui/icons/Mail";
import TextFormatIcon from "@material-ui/icons/TextFormat";
import LogoutIcon from "@material-ui/icons/ExitToApp";
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AccountBoxIcon from "@material-ui/icons/AccountBox";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import HomeIcon from "@material-ui/icons/Home";
import Router from "next/router";
import { logout } from 'services/browser/FirebaseWeb'
import { ApiCategory, getCategorySlug, getCategoryName } from "data/api/ApiCategory";
import CategoryIcon from "@material-ui/icons/LibraryBooks";
import Divider from "@material-ui/core/Divider";
import { Box } from '@chakra-ui/core'
import { useUserContext } from "contexts/new/UserContext";
import { useNavbarMenuState } from "components/optimized/NavbarMenu/NavbarMenu.hooks";

const MenuContainer = styled.div.attrs({ role: "presentation" })`
  width: 250px;
  overflow: hidden;
`;

const ListContainer = styled.div`
  height: 100vh;
  overflow-x: scroll;
`;

const CategoriesSectionTitle = styled<any>(ListItemText)`
  * {
    font-family: ${props => props.theme.fonts.poppins};
    font-weight: bold;
    margin-left: -10px;
    :hover {
      background-color: white;
    }
  }
`;

const MenuItemText = styled<any>(ListItemText)`
  * {
    font-family: ${props => props.theme.fonts.poppins};
  }
`;


const LoggedOutMenuItems = () => (
  <>
    <ListItem button key="login" onClick={() => Router.push("/login")}>
      <ListItemIcon>
        <AccountBoxIcon />
      </ListItemIcon>
      <MenuItemText primary={"Login"} />
    </ListItem>
    <ListItem
      button
      key="newsletter"
      onClick={() => Router.push("/newsletter")}
    >
      <ListItemIcon>
        <MailIcon />
      </ListItemIcon>
      <MenuItemText primary={"Newsletter"} />
    </ListItem>
    <ListItem button key="about-us" onClick={() => Router.push("/about")}>
      <ListItemIcon>
        <TextFormatIcon />
      </ListItemIcon>
      <MenuItemText primary={"About Us"} />
    </ListItem>
    <ListItem button key="gift" onClick={() => Router.push("/gift")}>
      <ListItemIcon>
        <CardGiftcardIcon />
      </ListItemIcon>
      <MenuItemText primary={"Gift a Subscription"} />
    </ListItem>
    <ListItem button key="shop" onClick={() => window.location.assign('https://the-juggernaut-store.myshopify.com/')}>
        <ListItemIcon>
            <ShoppingCartIcon />
        </ListItemIcon>
        <ListItemText primary={"Shop"} />
    </ListItem>
  </>
);

const LoggedInMenuItems = () => {
  const user = useUserContext()

  return (
    <>
      <ListItem button key="login" onClick={() => Router.push("/settings")}>
        <ListItemIcon>
          <AccountBoxIcon />
        </ListItemIcon>
        <MenuItemText primary={"My Account"} />
      </ListItem>
      <ListItem
        button
        key="newsletter"
        onClick={() => Router.push("/newsletter")}
      >
        <ListItemIcon>
          <MailIcon />
        </ListItemIcon>
        <MenuItemText primary={"Newsletter"} />
      </ListItem>
      <ListItem button key="about-us" onClick={() => Router.push("/about")}>
        <ListItemIcon>
          <TextFormatIcon />
        </ListItemIcon>
        <MenuItemText primary={"About Us"} />
      </ListItem>
      <ListItem button key="gift" onClick={() => Router.push("/gift")}>
        <ListItemIcon>
          <CardGiftcardIcon />
        </ListItemIcon>
        <MenuItemText primary={"Gift a Subscription"} />
      </ListItem>
      <ListItem button key="shop" onClick={() => window.location.assign('https://the-juggernaut-store.myshopify.com/')}>
          <ListItemIcon>
              <ShoppingCartIcon />
          </ListItemIcon>
          <ListItemText primary={"Shop"} />
      </ListItem>
      <ListItem
        button
        key="logout"
        onClick={() => {
          logout().then(() => Router.push({ pathname: '/' }));
        }}
      >
        <ListItemIcon>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary={"Log Out"} />
      </ListItem>
    </>
  )
} 

const CategoriesMenu = ({ categories }) => (
  <>
    <ListItem>
      <ListItemIcon>
        <CategoryIcon />
      </ListItemIcon>
      <CategoriesSectionTitle primary="Categories" />
    </ListItem>
    {categories
      .sort((a, b) => {
        return a.name < b.name ? -1 : 1;
      })
      .map(category => (
        <List component="div" disablePadding>
          <ListItem
            button
            onClick={() =>
              Router.push({
                pathname: `/category/${getCategorySlug(category)}`
              })
            }
          >
            <Box pl="50px">
              <MenuItemText primary={getCategoryName(category)} />
            </Box>
          </ListItem>
        </List>
      ))}
  </>
);

interface NavbarMenuProps {
    loggedIn: boolean,
    toggleMenu: () => void 
    open: boolean,
    categories: ApiCategory[]
}

export function NavbarMenu(props: NavbarMenuProps) {
    const { open, toggleMenu, loggedIn, categories } = props
    return (
      <Drawer open={open} onClose={() => toggleMenu()}>
        <MenuContainer
          onClick={() => toggleMenu()}
          onKeyDown={() => toggleMenu()}
        >
          <ListContainer>
            <ListItem button key="home" onClick={() => Router.push("/")}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <MenuItemText primary={"Home"} />
            </ListItem>
            {loggedIn ? <LoggedInMenuItems /> : <LoggedOutMenuItems />}
            <Box my={4}>
              <Divider />
            </Box>
            <CategoriesMenu categories={categories} />
          </ListContainer>
        </MenuContainer>
      </Drawer>
    );
};

NavbarMenu.defaultProps = {
    loggedIn: false,
    toggleMenu: (open: boolean) => (e: any) => {},
    open: false,
    categories: []
}

export function NavbarMenuWithState({ open, toggleMenu }) {
    const { categories, userIsLoggedIn } = useNavbarMenuState()

    return (
        <NavbarMenu
            loggedIn={userIsLoggedIn}
            toggleMenu={toggleMenu}
            open={open}
            categories={categories}
        />
    )
}
